import React, { useCallback, useMemo, useState } from 'react';
import { Subscription } from '../../../../../types/subscription';
import InputField from '../../../../../components/Form/InputField';
import { Validations } from '../../../../../components/Form';
import { colors } from '../../../../../colors';
import {
  useGetAllAvailableBalancesFromExchangesQuery,
  useGetAvailableBalanceQuery,
  useGetTopBotsQuery,
  useUpdateSubscriptionBalanceMutation,
  useUpdateSubscriptionPairMutation,
} from '../../../../../store/services/api';
import { useSelector } from 'react-redux';
import { selectUserId } from '../../../../../store/slices/user';
import { formatNumber } from '../../../../../helpers/NumberFormatter';
import Checkbox from '../../../../../components/Checkbox';
import LoadingIcons from 'react-loading-icons';
import DropdownComponent from '../../../../../components/Form/Dropdown';
import { Exchange } from 'grts-utils/src/types/Exchange';

interface Props {
  subscription: Subscription;
  maxDecimals: number | undefined;
}

// Show a basic input with base and asset quote
// If changing base asset, ask if we want to sell the asset as well.

// Also show move balance, but say to be added in the future

const ChangePair = (props: Props) => {
  const { subscription, maxDecimals } = props;

  const userId = useSelector(selectUserId);
  const [updateSubscriptionPair] = useUpdateSubscriptionPairMutation();
  const [isSuccess, setIsSuccess] = useState<boolean | undefined>();
  const [error, setError] = useState<string | undefined>();

  const { data: botsData } = useGetTopBotsQuery(
    { userId, query: subscription.baseAsset },
    { refetchOnMountOrArgChange: true },
  );
  const { data: balancesData, refetch } =
    useGetAllAvailableBalancesFromExchangesQuery(userId);

  const [selectedQuoteAsset, setSelectedQuoteAsset] = useState(
    subscription.quoteAsset,
  );
  const [selectedExchange, setSelectedExchange] = useState(
    subscription.exchange,
  );
  const [shouldMakeTransfer, setShouldMakeTransfer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const quoteAssets = useMemo(() => {
    const allQuoteAssets =
      botsData?.assets?.[subscription.baseAsset]
        ?.filter((p) => p.exchange === selectedExchange)
        ?.map((p) => p.quoteAsset)
        .filter((p, i, s) => s.findIndex((sp) => sp === p) === i) ?? [];

    return allQuoteAssets;
  }, [botsData?.assets, selectedExchange, subscription.baseAsset]);

  const exchanges = useMemo(() => {
    const allExchanges =
      botsData?.assets?.[subscription.baseAsset]
        ?.map((p) => p.exchange)
        .filter((p, i, s) => s.findIndex((sp) => sp === p) === i) ?? [];

    return allExchanges;
  }, [botsData?.assets, subscription.baseAsset]);

  const handleExchangeChange = useCallback(
    (exchange: Exchange) => {
      setSelectedExchange(exchange);

      const quoteAssets = botsData?.assets?.[subscription.baseAsset]
        .filter((p) => p.exchange === exchange)
        .map((p) => p.quoteAsset);

      if (!quoteAssets?.length) {
        return;
      }

      if (quoteAssets?.includes(selectedQuoteAsset)) {
        return;
      }

      setSelectedQuoteAsset(quoteAssets[0]);
    },
    [botsData?.assets, selectedQuoteAsset, subscription.baseAsset],
  );

  const handleQuoteAssetChange = (quoteAsset: string) => {
    setSelectedQuoteAsset(quoteAsset);
  };

  const originalStableBalance = subscription.stableBalance;
  const originalAssetBalance = subscription.assetBalance;
  const availableStableBalance = useMemo(() => {
    return (
      balancesData?.balances?.[selectedExchange]?.[selectedQuoteAsset] ?? 0
    );
  }, [balancesData?.balances, selectedExchange, selectedQuoteAsset]);
  const availableAssetBalance = useMemo(() => {
    return (
      balancesData?.balances?.[selectedExchange]?.[subscription.baseAsset] ?? 0
    );
  }, [balancesData?.balances, selectedExchange, subscription.baseAsset]);

  const hasEnoughStableBalance =
    availableStableBalance >= originalStableBalance;
  const hasEnoughAssetBalance =
    selectedExchange === subscription.exchange ||
    availableAssetBalance >= originalAssetBalance;

  const isButtonDisabled =
    !exchanges?.length ||
    !quoteAssets?.length ||
    (selectedExchange === subscription.exchange &&
      selectedQuoteAsset === subscription.quoteAsset) ||
    !hasEnoughStableBalance ||
    !hasEnoughAssetBalance;

  const showBalanceTransferOption =
    selectedExchange === subscription.exchange &&
    selectedQuoteAsset !== subscription.quoteAsset &&
    !hasEnoughStableBalance;

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    const tt = await updateSubscriptionPair({
      userId,
      subscriptionId: subscription.id,
      quoteAsset: selectedQuoteAsset,
      exchange: selectedExchange,
      shouldMakeTransfer,
    });

    setIsSuccess(!!tt.data?.success);
    setError(tt.data?.error);
    setIsLoading(false);

    if (!!tt.data?.success) {
      refetch();
    }
  }, [
    refetch,
    selectedExchange,
    selectedQuoteAsset,
    shouldMakeTransfer,
    subscription.id,
    updateSubscriptionPair,
    userId,
  ]);

  return (
    <div
      style={{
        paddingLeft: 16,
        paddingRight: 16,
        marginTop: -5,
        paddingBottom: 32,
        borderBottom: '0.5px solid rgba(80, 80, 80, 0.3)',
      }}
    >
      <p
        style={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: 20,
          marginBottom: 5,
        }}
      >
        Change Pair
      </p>
      <p
        style={{
          color: colors.green,
          marginTop: 0,
          marginBottom: -5,
          textTransform: 'uppercase',
          fontSize: 12,
        }}
      >
        Availability
      </p>
      <span style={{ color: colors.white }}>
        <span style={{ color: colors.gray }}>{subscription.quoteAsset}: </span>
        {formatNumber(availableStableBalance, 2)}
        <span style={{ color: colors.gray, marginLeft: 15 }}>
          {subscription.baseAsset}:{' '}
        </span>
        {formatNumber(availableAssetBalance, maxDecimals)}
      </span>
      <div
        style={{
          display: 'flex',
          columnGap: 16,
          marginBottom: 4,
          marginTop: 16,
        }}
      >
        <DropdownComponent
          config={{
            label: 'Exchange',
            value: selectedExchange,
            handleValueChange: handleExchangeChange,
            options: exchanges,
            isDisabled: exchanges.length < 2,
          }}
        />
        <DropdownComponent
          config={{
            label: 'QuoteAsset',
            value: selectedQuoteAsset,
            handleValueChange: handleQuoteAssetChange,
            options: quoteAssets,
            isDisabled: quoteAssets.length < 2,
          }}
        />
      </div>
      <div style={{ marginBottom: 16 }}>
        {!hasEnoughStableBalance && (
          <p style={{ color: colors.orange, margin: 0 }}>
            {`You don't have enough ${selectedQuoteAsset} on ${selectedExchange} to switch to this pair.`}
          </p>
        )}
        {!hasEnoughAssetBalance && (
          <p style={{ color: colors.orange, margin: 0 }}>
            {`You don't have enough ${subscription.baseAsset} on ${selectedExchange} to switch to this pair.`}
          </p>
        )}
        {isSuccess && (
          <p style={{ color: colors.green, margin: 0 }}>
            {'New pair successfully selected for this subscription'}
          </p>
        )}
        {error && <p style={{ color: colors.red, margin: 0 }}>{error}</p>}
      </div>

      {selectedExchange !== subscription.exchange && (
        <p style={{ color: colors.orange }}>
          Note: If you are changing exchanges, make sure you already have enough
          funds on that exchange before you continue here.
        </p>
      )}

      {showBalanceTransferOption && (
        <p style={{ color: colors.orange }}>
          <Checkbox
            label={`Sell the ${subscription.quoteAsset} balance from this bot to ${selectedQuoteAsset}. This makes sure you have enough ${selectedQuoteAsset} balance to make this switch.`}
            color={colors.orange}
            value={shouldMakeTransfer}
            handleValueChange={setShouldMakeTransfer}
          />
        </p>
      )}

      <button
        style={{
          backgroundColor: isButtonDisabled ? colors.gray : colors.green,
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 8,
          paddingBottom: 8,
          color: colors.white,
          borderRadius: 4,
          fontWeight: 800,
          border: 0,
          cursor: isButtonDisabled ? 'default' : 'pointer',
        }}
        // disabled={isButtonDisabled}
        onClick={handleSubmit}
      >
        {isLoading ? <LoadingIcons.Grid width={15} height={15} /> : 'Confirm'}
      </button>
    </div>
  );
};

const ChangeBalance = (props: Props) => {
  const { subscription, maxDecimals } = props;

  const userId = useSelector(selectUserId);

  const [updateSubscriptionBalance] = useUpdateSubscriptionBalanceMutation();
  const [isLoading, setIsLoading] = useState(false);

  const { data: availableStableBalance, refetch: refetchStableBalance } =
    useGetAvailableBalanceQuery({
      userId,
      subscriptionId: subscription.id,
      asset: subscription.quoteAsset,
    });

  const { data: availableAssetBalance, refetch: refetchAssetBalance } =
    useGetAvailableBalanceQuery({
      userId,
      subscriptionId: subscription.id,
      asset: subscription.baseAsset,
    });

  const [stableBalance, setStableBalance] = useState(
    subscription.stableBalance.toString(),
  );
  const [assetBalance, setAssetBalance] = useState(
    subscription.assetBalance.toString(),
  );

  const [
    shouldSellAssetBalanceDifference,
    setShouldSellAssetBalanceDifference,
  ] = useState(false);
  const [shouldBuyAssetBalanceDifference, setShouldBuyAssetBalanceDifference] =
    useState(false);

  const didStableBalanceChange =
    parseFloat(stableBalance) !== subscription.stableBalance;
  const didAssetBalanceChange =
    parseFloat(assetBalance) !== subscription.assetBalance;

  const showTooMuchQuoteAsset =
    didStableBalanceChange &&
    typeof availableStableBalance?.balance !== 'undefined'
      ? parseFloat(stableBalance) > availableStableBalance?.balance
      : false;

  const showTooMuchBaseAsset =
    didAssetBalanceChange &&
    typeof availableAssetBalance?.balance !== 'undefined'
      ? parseFloat(assetBalance) >
        availableAssetBalance?.balance + subscription.assetBalance
      : false;

  const isAssetBalanceLower =
    didAssetBalanceChange &&
    parseFloat(assetBalance) < subscription.assetBalance;
  const isAssetBalanceHigher =
    didAssetBalanceChange &&
    parseFloat(assetBalance) >
      subscription.assetBalance + (availableAssetBalance?.balance || 0);

  const maxAavailableStableBalanceToAdd = availableStableBalance?.balance
    ? availableStableBalance.balance - parseFloat(stableBalance || '0')
    : 0;

  const maxAvailableAssetBalanceToAdd = availableStableBalance?.balance
    ? maxAavailableStableBalanceToAdd / subscription.latestPrice
    : 0;

  const exceededMaximumAssetBalance =
    parseFloat(assetBalance) >
    maxAvailableAssetBalanceToAdd + subscription.assetBalance;

  const stableBalanceError = didStableBalanceChange
    ? isNaN(parseFloat(stableBalance))
      ? Validations.NumberTyping
      : parseFloat(stableBalance) < 0
      ? Validations.NonZero
      : showTooMuchQuoteAsset
      ? Validations.MaximumExceeded
      : undefined
    : undefined;

  const assetBalanceError = didAssetBalanceChange
    ? isNaN(parseFloat(assetBalance))
      ? Validations.NumberTyping
      : parseFloat(assetBalance) < 0
      ? Validations.NonZero
      : (
          isAssetBalanceHigher && shouldBuyAssetBalanceDifference
            ? exceededMaximumAssetBalance
            : showTooMuchBaseAsset
        )
      ? Validations.MaximumExceeded
      : undefined
    : undefined;

  const isButtonDisabled =
    (!didStableBalanceChange && !didAssetBalanceChange) ||
    !!stableBalanceError ||
    !!assetBalanceError;

  const handleSubmit = useCallback(async () => {
    if (isButtonDisabled) {
      return;
    }

    setIsLoading(true);

    await updateSubscriptionBalance({
      userId: userId,
      subscriptionId: subscription.id,
      stableBalance: didStableBalanceChange
        ? parseFloat(stableBalance)
        : undefined,
      assetBalance: didAssetBalanceChange
        ? parseFloat(assetBalance)
        : undefined,
      shouldOrder:
        shouldSellAssetBalanceDifference || shouldBuyAssetBalanceDifference,
    });

    setIsLoading(false);

    refetchStableBalance();
    refetchAssetBalance();
  }, [
    assetBalance,
    didAssetBalanceChange,
    didStableBalanceChange,
    isButtonDisabled,
    refetchAssetBalance,
    refetchStableBalance,
    shouldBuyAssetBalanceDifference,
    shouldSellAssetBalanceDifference,
    stableBalance,
    subscription.id,
    updateSubscriptionBalance,
    userId,
  ]);

  return (
    <div
      style={{
        paddingLeft: 16,
        paddingRight: 16,
        marginTop: -5,
        paddingBottom: 32,
        borderBottom: '0.5px solid rgba(80, 80, 80, 0.3)',
      }}
    >
      <p
        style={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: 20,
          marginBottom: 5,
        }}
      >
        Change Balance
      </p>
      <div style={{ marginBottom: 20 }}>
        <p
          style={{
            color: colors.green,
            marginTop: 0,
            marginBottom: -5,
            textTransform: 'uppercase',
            fontSize: 12,
          }}
        >
          Availability
        </p>
        {typeof availableStableBalance?.balance !== 'undefined' &&
          typeof availableAssetBalance?.balance !== 'undefined' && (
            <span style={{ color: colors.white }}>
              <span style={{ color: colors.gray }}>
                {subscription.quoteAsset}:{' '}
              </span>
              {formatNumber(availableStableBalance?.balance, 2)}
              <span style={{ color: colors.gray, marginLeft: 15 }}>
                {subscription.baseAsset}:{' '}
              </span>
              {formatNumber(availableAssetBalance.balance, maxDecimals)}
            </span>
          )}
      </div>
      <div style={{ display: 'flex', columnGap: 16, marginBottom: 16 }}>
        <InputField
          config={{
            label: `Stable Balance (${subscription.stableBalance})`,
            value: stableBalance ? stableBalance.toString() : '',
            handleValueChange: setStableBalance,
            shouldFit: true,
            error: stableBalanceError,
          }}
        />
        <InputField
          config={{
            label: `Asset Balance (${subscription.assetBalance})`,
            value: assetBalance,
            handleValueChange: setAssetBalance,
            shouldFit: true,
            error: assetBalanceError,
          }}
        />
      </div>
      {isAssetBalanceLower && (
        <p style={{ color: colors.orange }}>
          <Checkbox
            label=" The asset balance you entered is lower than your current susbcription
          balance. Would you like the program to sell the difference in this
          balance?"
            color={colors.orange}
            value={shouldSellAssetBalanceDifference}
            handleValueChange={setShouldSellAssetBalanceDifference}
          />
        </p>
      )}
      {isAssetBalanceHigher && (
        <p style={{ color: colors.orange }}>
          <Checkbox
            label={`The asset balance you entered is higher than your current subscription
          balance. Your available exchange asset balance allows you to add an
          other ${formatNumber(maxAvailableAssetBalanceToAdd, maxDecimals)}
          ${subscription.baseAsset}. ${
              exceededMaximumAssetBalance
                ? 'At this moment, the asset balance you entered exceeds the maximum amount you are able to add.'
                : 'Confirming will result into the program using your exchange stable balance to buy more ' +
                  subscription.baseAsset +
                  '.'
            }`}
            isDisabled={exceededMaximumAssetBalance}
            color={colors.orange}
            value={
              exceededMaximumAssetBalance
                ? false
                : shouldBuyAssetBalanceDifference
            }
            handleValueChange={setShouldBuyAssetBalanceDifference}
          />
        </p>
      )}
      <button
        style={{
          backgroundColor: isButtonDisabled ? colors.gray : colors.green,
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 8,
          paddingBottom: 8,
          color: colors.white,
          borderRadius: 4,
          fontWeight: 800,
          border: 0,
          cursor: isButtonDisabled ? 'default' : 'pointer',
        }}
        disabled={isButtonDisabled}
        onClick={handleSubmit}
      >
        {isLoading ? <LoadingIcons.Grid width={15} height={15} /> : 'Confirm'}
      </button>
    </div>
  );
};

const MoveBalance = () => {
  return (
    <div
      style={{
        paddingLeft: 16,
        paddingRight: 16,
        marginTop: -5,
        paddingBottom: 32,
        borderBottom: '0.5px solid rgba(80, 80, 80, 0.3)',
      }}
    >
      <p
        style={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: 20,
          marginBottom: 5,
        }}
      >
        Move Balance
      </p>
      <p style={{ color: colors.orange, fontWeight: 'bold', fontSize: 12 }}>
        This feature will be build in the future. This is simply a shortcut to
        move balance to other subscriptions. You can already achieve this by
        removing balance from this subscription and manually adding it to an
        other subscription.
      </p>
    </div>
  );
};

const ManageBalance = (props: Props) => {
  return (
    <>
      <ChangePair {...props} />
      <ChangeBalance {...props} />
      <MoveBalance />
    </>
  );
};

export default ManageBalance;
